import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { isErrorWithData } from '@/services/helpers';
import {
  useGetWelcomeCallStipRulesQuery,
  useUpdateWelcomeCallEnabledMutation,
  useUpdateWelcomeCallStipRulesMutation,
} from '@/services/welcomeCallStipRules';
import {
  PalmettoButton,
  PalmettoModal,
  PalmettoSelect,
  PalmettoTag,
  PalmettoTextInput,
} from '@palmetto/dsm-components';
import { Alert, toast } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useBlocker } from 'react-router-dom';

import {
  Clientize,
  Locale,
  ProgramType,
  ProgramTypes,
  ProgramTypeLabels,
  WelcomeCallProgramConfig,
  WelcomeCallStipCriterion,
  WelcomeCallStipRulesValue,
} from 'types';

import { ChangeHistory } from './ChangeHistory';
import { LabeledToggle } from './LabeledToggle';
import { RuleCard } from './RuleCard';
import {
  DEFAULT_FORM_VALUES,
  MultiCriterionFormValue,
  NumericCriterionFormValue,
  RULE_CARD_CONFIGS,
  SALES_REP_STATUS_OPTIONS,
  WelcomeCallStipRulesFormValues,
} from './types';

const numericToForm = (criterion?: {
  enabled: boolean;
  value?: number;
  applicationRate: number;
}): NumericCriterionFormValue => ({
  enabled: criterion?.enabled ?? false,
  value: criterion?.value !== undefined ? String(criterion.value) : '',
  applicationRate: String(criterion?.applicationRate ?? 100),
});

// One entry per selected item, '' meaning "no override". RuleCard mounts a Controller per selected item, and
// react-hook-form's dirty check is key-count sensitive: a missing default key becomes an `undefined` value key
// on mount, so sparse maps would leave the form dirty after every reset.
const denseByItem = (items: string[], overrides: Record<string, string | number> | undefined) =>
  Object.fromEntries(items.map((item) => [item, overrides?.[item] !== undefined ? String(overrides[item]) : '']));

const multiToForm = (criterion?: {
  enabled: boolean;
  value?: string[];
  applicationRate: number;
  applicationRates?: Record<string, number>;
}): MultiCriterionFormValue => ({
  enabled: criterion?.enabled ?? false,
  value: criterion?.value ?? [],
  applicationRate: String(criterion?.applicationRate ?? 100),
  applicationRates: denseByItem(criterion?.value ?? [], criterion?.applicationRates),
});

export const toFormValues = (
  doc: Clientize<WelcomeCallProgramConfig> | undefined,
): WelcomeCallStipRulesFormValues => {
  if (!doc) return DEFAULT_FORM_VALUES;
  return {
    templateId: doc.templateId,
    age: numericToForm(doc.value.age),
    state: {
      ...multiToForm(doc.value.state),
      templateIds: denseByItem(doc.value.state.value ?? [], doc.value.state.templateIds),
    },
    salesRepStatus: multiToForm(doc.value.salesRepStatus),
    quoteMonthlyPayment: numericToForm(doc.value.quoteMonthlyPayment),
    preferredLanguage: multiToForm(doc.value.preferredLanguage),
  };
};

// The rate Controller unmounts when a criterion is disabled, so a stale non-integer rate could
// otherwise bypass inline validation and 400 against the API's unconditional rate check.
const clampRate = (rate: string): number => Math.min(100, Math.max(0, Math.round(Number(rate) || 0)));

// Builds per-item rates for selected items that have an explicit override. Items with a blank
// or absent rate are intentionally omitted so the evaluator falls back to applicationRate,
// preserving "inherit from default" intent across future default-rate changes.
const buildApplicationRates = (
  rates: Record<string, string | undefined>,
  selectedValues: string[],
): { applicationRates?: Record<string, number> } => {
  const entries = selectedValues.flatMap((item) => {
    const explicit = rates[item];
    if (explicit === '' || explicit === undefined) return [];
    return [[item, clampRate(explicit)] as [string, number]];
  });
  return entries.length > 0 ? { applicationRates: Object.fromEntries(entries) } : {};
};

// Same blank-means-inherit rule as buildApplicationRates: a state without its own template id
// falls back to the program template at call time.
const buildTemplateIds = (
  templateIds: Record<string, string | undefined> | undefined,
  selectedValues: string[],
): { templateIds?: Record<string, string> } => {
  const entries = selectedValues.flatMap((item) => {
    const explicit = templateIds?.[item]?.trim();
    return explicit ? [[item, explicit] as [string, string]] : [];
  });
  return entries.length > 0 ? { templateIds: Object.fromEntries(entries) } : {};
};

export const toPayload = (values: WelcomeCallStipRulesFormValues): WelcomeCallStipRulesValue => {
  const numeric = (formCriterion: NumericCriterionFormValue): WelcomeCallStipRulesValue['age'] => ({
    enabled: formCriterion.enabled,
    ...(formCriterion.enabled ? { value: Number(formCriterion.value) } : {}),
    applicationRate: clampRate(formCriterion.applicationRate),
  });
  const multi = (formCriterion: MultiCriterionFormValue): WelcomeCallStipRulesValue['state'] => ({
    enabled: formCriterion.enabled,
    ...(formCriterion.enabled ? { value: formCriterion.value } : {}),
    applicationRate: clampRate(formCriterion.applicationRate),
    ...(formCriterion.enabled ? buildApplicationRates(formCriterion.applicationRates, formCriterion.value) : {}),
  });
  const state = (formCriterion: MultiCriterionFormValue): WelcomeCallStipRulesValue['state'] => ({
    ...multi(formCriterion),
    ...(formCriterion.enabled ? buildTemplateIds(formCriterion.templateIds, formCriterion.value) : {}),
  });
  // Narrows the form layer's plain string[] down to the enum-array types the payload
  // requires via a type-predicate filter, not an assertion — a stray value that isn't a
  // real enum member is dropped rather than cast through.
  const multiEnum = <T extends string>(
    formCriterion: MultiCriterionFormValue,
    allowed: readonly T[],
  ): WelcomeCallStipCriterion<T[]> => {
    const allowedValues: readonly string[] = allowed;
    const filteredValue = formCriterion.value.filter((item): item is T => allowedValues.includes(item));
    return {
      enabled: formCriterion.enabled,
      ...(formCriterion.enabled ? { value: filteredValue } : {}),
      applicationRate: clampRate(formCriterion.applicationRate),
      ...(formCriterion.enabled ? buildApplicationRates(formCriterion.applicationRates, filteredValue) : {}),
    };
  };
  return {
    age: numeric(values.age),
    state: state(values.state),
    salesRepStatus: multiEnum(
      values.salesRepStatus,
      SALES_REP_STATUS_OPTIONS.map((option) => option.value),
    ),
    quoteMonthlyPayment: numeric(values.quoteMonthlyPayment),
    preferredLanguage: multiEnum(values.preferredLanguage, Object.values(Locale)),
  };
};

const EVALUATION_INFO_MESSAGE = (
  <ul className="list-disc pl-16">
    <li>Each criterion is evaluated independently — any one match can trigger the stipulation.</li>
    <li>Numeric criteria use a fixed greater-than-or-equal-to comparison.</li>
    <li>Multi-select criteria match if the account value is any one of the selected options.</li>
    <li>
      When a criterion matches, its Apply Rate decides — as a percentage chance — whether the stipulation is actually
      applied.
    </li>
  </ul>
);

const PROGRAM_OPTIONS = ProgramTypes.map((value) => ({
  value,
  label: ProgramTypeLabels[value],
}));

export const WelcomeCallStipRules = () => {
  const [programType, setProgramType] = useState<ProgramType>(ProgramType.solar);
  const { data, isLoading, isFetching, isError } = useGetWelcomeCallStipRulesQuery(programType);
  const [updateRules, { isLoading: isSaving }] = useUpdateWelcomeCallStipRulesMutation();
  const [updateEnabled, { isLoading: isUpdatingEnabled }] = useUpdateWelcomeCallEnabledMutation();
  // The requested value awaiting confirmation; null when no dialog is open.
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, errors },
  } = useForm<WelcomeCallStipRulesFormValues>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_FORM_VALUES,
  });

  useEffect(() => {
    if (data) reset(toFormValues(data));
  }, [data, reset]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleCancel = useCallback(() => {
    reset(toFormValues(data));
  }, [reset, data]);

  const onSubmit = useCallback(
    async (values: WelcomeCallStipRulesFormValues) => {
      try {
        const saved = await updateRules({
          programType,
          templateId: values.templateId.trim(),
          value: toPayload(values),
        }).unwrap();
        reset(toFormValues(saved));
        toast.success('Welcome call stip rules saved');
      } catch (err) {
        if (isErrorWithData(err)) {
          toast.error(err.data.message);
        } else {
          toast.error('Failed to save welcome call stip rules. Please try again.');
        }
      }
    },
    [updateRules, reset, programType],
  );

  const onInvalid = useCallback(() => {
    if (errors.templateId) toast.error(errors.templateId.message ?? 'Set the Recheck Template ID first.');
  }, [errors]);

  const handleProgramChange = useCallback((details: { value: string[] }) => {
    const next = details.value[0] as ProgramType | undefined;
    if (next) setProgramType(next);
  }, []);

  const programItems = useMemo(
    () =>
      PROGRAM_OPTIONS.map((opt) => ({
        ...opt,
        selected: opt.value === programType,
      })),
    [programType],
  );

  const handleCancelEnabledChange = useCallback(() => setPendingEnabled(null), []);

  const handleConfirmEnabledChange = useCallback(async () => {
    if (pendingEnabled === null) return;
    try {
      await updateEnabled({ programType, enabled: pendingEnabled }).unwrap();
      toast.success(`Welcome call ${pendingEnabled ? 'enabled' : 'disabled'} for ${ProgramTypeLabels[programType]}`);
    } catch (err) {
      toast.error(isErrorWithData(err) ? err.data.message : 'Failed to update welcome call. Please try again.');
    } finally {
      setPendingEnabled(null);
    }
  }, [pendingEnabled, updateEnabled, programType]);

  const handleBlockerReset = useCallback(() => blocker.reset?.(), [blocker]);
  const handleBlockerProceed = useCallback(() => blocker.proceed?.(), [blocker]);

  const programLabel = ProgramTypeLabels[programType];

  return (
    <DsmPageContainer title="Welcome Call Stip Rules">
      <PageHeader title="Welcome Call Stip Rules" />
      <div className="mx-6 flex flex-col gap-16">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8">
            <p className="text-sm text-text-secondary">
              Configure the Recheck template and the criteria that trigger a welcome call stipulation, per program.
            </p>
            <PalmettoTag label="Restricted to Underwriting Admin" color="warning" />
          </div>
          <div className="flex gap-4">
            <PalmettoButton
              type="button"
              emphasis="secondary"
              layout="hug"
              onClick={handleCancel}
              disabled={!isDirty || isSaving}
            >
              Cancel
            </PalmettoButton>
            <PalmettoButton
              type="submit"
              form="welcome-call-stip-rules-form"
              emphasis="primary"
              layout="hug"
              disabled={!isDirty || isLoading || isFetching || isSaving || isError}
            >
              {isSaving ? 'Saving...' : 'Save Rules'}
            </PalmettoButton>
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-16">
          <div className="w-[260px]">
            <PalmettoSelect
              label="Program"
              items={programItems}
              onValueChange={handleProgramChange}
              disabled={isDirty || isSaving}
            />
            {isDirty && (
              <p className="mt-4 text-xs text-text-secondary">Save or cancel your changes to switch program.</p>
            )}
          </div>
          <div className="w-[400px]">
            <Controller
              name="templateId"
              control={control}
              rules={{
                validate: (value: string) =>
                  value.trim() !== '' || `Set the ${programLabel} Recheck Template ID before saving rules.`,
              }}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-4">
                  <PalmettoTextInput
                    id="welcome-call-template-id"
                    label={`${programLabel} Recheck Template ID`}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={isLoading || isFetching || isSaving}
                    className={
                      fieldState.error ? 'border-border-error bg-input-bg-error outline-border-error' : undefined
                    }
                  />
                  {fieldState.error && <p className="text-xs text-text-error">{fieldState.error.message}</p>}
                </div>
              )}
            />
            <p className="mt-4 text-xs text-text-secondary">
              Used for every {programLabel} account unless a state below has its own. The ID is not checked against
              Recheck.
            </p>
          </div>
          {data?.isConfigured && (
            <div className="flex flex-col gap-4 pt-[20px]">
              <LabeledToggle
                id="welcome-call-program-enabled"
                label={`${programLabel} Welcome Call ${data.enabled ? 'Enabled' : 'Disabled'}`}
                ariaLabel={`Enable Welcome Call for ${programLabel}`}
                checked={data.enabled}
                onChange={setPendingEnabled}
                disabled={isLoading || isFetching || isSaving || isUpdatingEnabled || isDirty}
              />
              {isDirty && <p className="text-xs text-text-secondary">Save or cancel your changes first.</p>}
            </div>
          )}
        </div>

        <Alert variant="info" title="How evaluation works" message={EVALUATION_INFO_MESSAGE} />

        {isError ? (
          <p className="text-text-error">
            Couldn&apos;t load Welcome Call settings for {programLabel}. Refresh to try again; contact engineering if
            it persists.
          </p>
        ) : (
          <>
            {/* noValidate: the number inputs' min/max would otherwise trigger native validation and block submit before RHF renders our styled errors */}
            <form id="welcome-call-stip-rules-form" noValidate onSubmit={handleSubmit(onSubmit, onInvalid)}>
              <div className="flex flex-col gap-24">
                {RULE_CARD_CONFIGS.map((config) => (
                  <RuleCard
                    key={config.key}
                    config={config}
                    control={control}
                    setValue={setValue}
                    disabled={isLoading || isFetching || isSaving}
                  />
                ))}
              </div>
            </form>
          </>
        )}

        {!isError && <ChangeHistory programType={programType} />}
      </div>

      <PalmettoModal open={pendingEnabled !== null} onClose={handleCancelEnabledChange}>
        <p>
          {pendingEnabled
            ? `Enable Welcome Call for ${programLabel}? New ${programLabel} accounts will be evaluated against these rules again.`
            : `Disable Welcome Call for ${programLabel}? No new Welcome Call stipulations will be applied to ${programLabel} accounts while it is off.`}
        </p>
        <div className="mt-16 flex justify-end gap-8">
          <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleCancelEnabledChange}>
            Cancel
          </PalmettoButton>
          <PalmettoButton
            type="button"
            emphasis="primary"
            layout="hug"
            onClick={handleConfirmEnabledChange}
            disabled={isUpdatingEnabled}
          >
            {pendingEnabled ? 'Enable' : 'Disable'}
          </PalmettoButton>
        </div>
      </PalmettoModal>

      <PalmettoModal open={blocker.state === 'blocked'} onClose={handleBlockerReset}>
        <p>You have unsaved changes. Are you sure you want to leave?</p>
        <div className="mt-16 flex justify-end gap-8">
          <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleBlockerReset}>
            Stay
          </PalmettoButton>
          <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleBlockerProceed}>
            Leave anyway
          </PalmettoButton>
        </div>
      </PalmettoModal>
    </DsmPageContainer>
  );
};
